import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CoachingPlanStore from 'stores/coaching/CoachingPlanStore';
import {associateBy} from 'utils/associateBy';
import type User from 'records/User';
import type CoachingPlan from 'records/CoachingPlan';
import type {PlanId} from 'records/CoachingPlan';

interface CoachingPageHeaderProps {
  plan: CoachingPlan;
  rep: User;
  sessionUser: User;
  onSelectPlan: (planId: PlanId) => void;
  autoStartReview?: boolean;
}

@connectToStores
class CoachingPageHeader extends React.PureComponent<
  CoachingPageHeaderProps & ReturnType<typeof CoachingPageHeader.getPropsFromStores>
> {
  static getStores() {
    return [CoachingPlanStore];
  }

  static getPropsFromStores({rep}) {
    return {
      coachingPlans: CoachingPlanStore.getState()
        .planMap.toList()
        .filter(p => p.recipient.id === rep?.id),
    };
  }

  render() {
    const {coachingPlans, sessionUser, rep, plan, onSelectPlan, autoStartReview} = this.props;
    if (!plan || !rep) return null;

    const options = coachingPlans
      .filter(p => {
        const notComplete = !p.completed;
        const hasCompleteReview = !!p.reviews?.some(r => r.status === 'COMPLETED');

        if (p.unscheduled() && notComplete) {
          return false;
        }
        return notComplete || hasCompleteReview;
      })
      .sortBy(p => p.created)
      .reverse()
      .map(p => ({
        label: p.name + (p.completed ? ' (Complete)' : ''),
        value: p.id,
      }));
    const optionMap = associateBy(options, o => o.value);

    const title = sessionUser.id === rep.id ? plan.name : rep.getFullName();

    return (
      <header className="coaching-page-header">
        <h1>{title}</h1>
        {options.size > 1 && !autoStartReview && (
          <Autocomplete
            disableClearable
            autoHighlight
            className="plan-dropdown"
            renderInput={params => <TextField {...params} size="small" variant="outlined" />}
            options={options.toArray()}
            value={optionMap.get(plan.id)}
            getOptionLabel={opt => opt.label}
            getOptionSelected={(opt, selected) => opt.value === selected.value}
            onChange={(e, opt) => onSelectPlan(opt!.value)}
          />
        )}
      </header>
    );
  }
}

export default CoachingPageHeader as unknown as React.FC<CoachingPageHeaderProps>;
